import React from 'react';
import ReactMarkdown from 'react-markdown';

import { ParsedReference } from '../types';

interface ReferencesGridProps {
  content: string;
  sourceUris: string[];
  onOpenDocument: (uri: string) => void;
}

/** Parse markdown table rows into structured reference objects. */
export function parseReferences(content: string): ParsedReference[] {
  const refs: ParsedReference[] = [];
  const rows = content.match(/\|(.+)\|/g) || [];

  for (const row of rows) {
    const cells = row.split('|').filter((cell) => cell.trim());
    if (cells.length < 3) continue;

    const manual = cells[0].trim();
    const taskNumber = cells[1]?.trim() || '';
    const title = cells[2]?.trim() || '';
    const revision = cells[3]?.trim() || '';

    const isHeaderRow = manual.toLowerCase() === 'manual';
    const isSeparatorRow = /^[-:\s]+$/.test(manual) || /^[-:\s]+$/.test(taskNumber);
    if (isHeaderRow || isSeparatorRow) continue;

    refs.push({ manual, taskNumber, title, revision });
  }

  return refs;
}

/** Match a parsed reference to the most relevant source URI. */
function findSourceUri(ref: ParsedReference, sourceUris: string[]): string | undefined {
  const searchTerms = [ref.taskNumber, ref.manual].map(
    (term) => term.toLowerCase().replace(/[^a-z0-9]/g, ''),
  );

  for (const uri of sourceUris) {
    const normalizedUri = uri.toLowerCase().replace(/[^a-z0-9/]/g, '');
    if (searchTerms.some((term) => term && normalizedUri.includes(term))) {
      return uri;
    }
  }

  return sourceUris[0];
}

/** SVG book icon for reference cards. */
const BookIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
    <path d="M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1zm0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5z" />
  </svg>
);

/** Grouped reference data for a single source document. */
interface ReferenceGroup {
  uri: string;
  filename: string;
  tasks: ParsedReference[];
}

/** Build grouped reference cards from parsed references and source URIs. */
function buildReferenceGroups(
  refs: ParsedReference[],
  sourceUris: string[],
): ReferenceGroup[] {
  const refsWithUri = refs.map((ref) => ({
    ...ref,
    uri: findSourceUri(ref, sourceUris),
  }));

  const grouped: Record<string, ReferenceGroup> = {};

  for (const ref of refsWithUri) {
    const uri = ref.uri || 'unknown';
    if (!grouped[uri]) {
      const filename = uri !== 'unknown'
        ? (uri.split('/').pop() || 'Document')
        : ref.manual;
      grouped[uri] = { uri, filename, tasks: [] };
    }
    grouped[uri].tasks.push(ref);
  }

  for (const uri of sourceUris) {
    if (!grouped[uri]) {
      const filename = uri.split('/').pop() || 'Document';
      grouped[uri] = { uri, filename, tasks: [] };
    }
  }

  return Object.values(grouped);
}

/** Grid of reference cards grouped by source document with clickable icons. */
const ReferencesGrid: React.FC<ReferencesGridProps> = ({
  content,
  sourceUris,
  onOpenDocument,
}) => {
  const refs = parseReferences(content);

  if (refs.length === 0 && sourceUris.length === 0) {
    return <ReactMarkdown>{content}</ReactMarkdown>;
  }

  const groups = buildReferenceGroups(refs, sourceUris);

  return (
    <div className="ref-grid">
      {groups.map((group, index) => (
        <div key={index} className="ref-card">
          <button
            className="ref-icon-btn"
            onClick={() => group.uri !== 'unknown' && onOpenDocument(group.uri)}
            title={`Open: ${group.filename}`}
            disabled={group.uri === 'unknown'}
          >
            <BookIcon />
            <span className="ref-icon-label">{group.filename}</span>
          </button>
          {group.tasks.length > 0 && (
            <ul className="ref-task-list">
              {group.tasks.map((task, taskIndex) => (
                <li key={taskIndex} className="ref-task-item">
                  <span className="ref-task-number">{task.taskNumber}</span>
                  <span className="ref-task-title">{task.title}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
};

export default ReferencesGrid;
